import * as ENV            from '../../util/env';
import { useContext }      from 'react';
import { useEffect }       from 'react';
import { useRef }          from 'react';
import { useState }        from 'react';
import connectProgress     from '../google/connect_progress';
import FacebookAuthCordova from './facebook_auth_cordova';
import FacebookAuthWeb     from './facebook_auth_web';
import React               from 'react';
import useObjectState      from '../../hooks/use_object_state';

const initialState = {
  initialized: null,
  error:       null,
  accessToken: null,
  isSignedIn:  null
};

export default function FacebookAuth({ children }) {
  const [ state, updateState ] = useObjectState(initialState);

  const AuthComponent = (
    ENV.isCordova() ?
      FacebookAuthCordova :
      FacebookAuthWeb
  );

  return (
    <FacebookAuth.Context.Provider value={state}>
      <AuthComponent state={state} updateState={updateState}/>
      <FacebookAuth.Loading>
        <FacebookAuth.LoadingSpinnerView/>
      </FacebookAuth.Loading>
      <FacebookAuth.Failed>
        <FacebookAuth.LoadingFailedView/>
      </FacebookAuth.Failed>
      {children}
    </FacebookAuth.Context.Provider>
  );
}

FacebookAuth.Context             = React.createContext();
FacebookAuth.Context.displayName = 'FacebookAuthContext';

function Failed({ children }) {
  const { error } = useContext(FacebookAuth.Context);

  if (!error)
    return null;

  return children;
}

function Loading({ children }) {
  const { initialized, error } = useContext(FacebookAuth.Context);

  if (initialized || error)
    return null;

  return children;
}

function SignedIn({ children }) {
  const { initialized, isSignedIn } = useContext(FacebookAuth.Context);

  if (!initialized)
    return null;

  if (isSignedIn !== true)
    return null;

  return children;
}

function SignedOut({ children }) {
  const { initialized, isSignedIn } = useContext(FacebookAuth.Context);

  if (!initialized)
    return null;

  if (isSignedIn !== false)
    return null;

  return children;
}

FacebookAuth.Failed    = Failed;
FacebookAuth.Loading   = Loading;
FacebookAuth.SignedIn  = SignedIn;
FacebookAuth.SignedOut = SignedOut;

function LoadingSpinnerView({ progressStart, progressEnd }) {
  useEffect(() => {
    progressStart();
    return progressEnd;
  }, [ progressStart, progressEnd ]);
  return null;
}
FacebookAuth.LoadingSpinnerView = connectProgress(LoadingSpinnerView);


function LoadingFailedView() {
  throw new Error('Failed to load Facebook API.');
}
FacebookAuth.LoadingFailedView = LoadingFailedView;

export function useFacebookAccessToken() {
  const { accessToken } = useContext(FacebookAuth.Context);
  return accessToken;
}

export function useFacebookClient() {
  // For desktop, this is the FB client installed by Facebook's script tag. For
  // Cordova, this is an API-compatible wrapper around the
  // facebookConnectPlugin, installed by our FacebookAuthCordova component.
  return window.FB;
}

export function useFacebookPagesList({ progressStart, progressEnd }) {
  const [ status, setStatus ] = useState('pending');
  const [ result, setResult ] = useState();
  const FB                    = useFacebookClient();
  const mounted               = useRef();

  // effect to track mounted state so our listener is rendered inert when we dismount
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (!mounted.current)
      return () => {};

    if (status !== 'pending')
      return () => {};

    progressStart();

    FB.api('/me/accounts', 'GET', {}, response => {
      if (mounted.current) {
        if (response.data) {
          setResult(response.data);
          setStatus('succeeded');
        } else
          setStatus('failed');
      }

      progressEnd();
    });

    return () => {};
  }, [ FB, progressEnd, progressStart, result, status ]);

  return [ result, status ];
}
